import React, { useEffect, useRef, useState } from 'react';

import { observer } from 'mobx-react';
import { GameTitleBox } from '../../boxes/game-title/game-title.box';
import classNames from 'classnames';
import { ENABLE_HOVER_ANIMATION, MULTIPLE_LINES_TITLE, TWO_LINES_TITLE } from '../../../css-constants';
import { GameInfo } from '../../../../../services/games/game-info';
import { MinMaxBetAmountBox } from '../../boxes/min-max-bet-amount/min-max-bet-amount.box';
import { InfoPageButtonComponent } from '../info-page-button/info-page-button.component';
import { GameButtonsBox } from '../../boxes/game-buttons/game-buttons.box';
import { PlayDemoButtonComponent } from '../play-demo-button/play-demo-button.component';
import { GameDetailsContainerBox } from '../../boxes/game-details-container/game-details-container.box';
import { JackpotAmountBox } from '../../boxes/jackpot-amount/jackpot-amount.box';
import { GameJackpotComponent } from '../../../../game-jackpot/game-jackpot.component';
import { GameInfoContainerBox } from '../../boxes/game-info-container/game-info-container.box';
import { CyClass } from "../../../../../../common/cy-classes";
import { AmountContainerBox } from '../../boxes/amount-container/amount-container.box';
import { TwoRowsGameTitleContainerBox } from '../../boxes/two-rows-game-title-container/two-rows-game-title-container.box';
import { useServices } from "../../../../../react-hooks/use-services.hook";

export interface GameDetailsContainerComponentProps {
    gameInfo: GameInfo;
    disableGameInfoHover: boolean;
    onOpenAnonymousGame: () => void;
    onOpenInfoPage: () => void;
    squareGameIcon?: boolean;
    hasTwoLinesTitle: boolean;
}

export const GameDetailsContainerComponent: React.FC<GameDetailsContainerComponentProps> = observer((props) => {
    const services = useServices();
    const [hasInfoPage, setHasInfoPage] = useState(false);
    const titleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        props.gameInfo.hasInfoPage().then(hasInfoPage => {
            setHasInfoPage(hasInfoPage)
        })
    }, [props.gameInfo]);

    const _allowHoverDetails = () => {

        if (props.disableGameInfoHover || services.device.isMobile || !props.gameInfo.allowHoverDetails) {
            return false;
        }

        return Boolean(hasInfoPage || props.gameInfo.allowDemoButtonInSingleGame);
    }

    useEffect(() => {
        if (!titleRef?.current) return;

        const titleElement = titleRef.current;
        const titleHeight = titleElement?.offsetHeight || 0;
        const titleLineHeight = +window.getComputedStyle(titleElement).getPropertyValue('line-height').substring(0, 2);
        const maxElSize = titleLineHeight * 2 + 1;

        if (titleHeight > maxElSize) {
            titleElement.style.maxHeight = maxElSize + 'px';
            titleElement.classList.add(MULTIPLE_LINES_TITLE);
        };
    }, [titleRef])


    const _renderGameButtons = () => {
        if (!_allowHoverDetails()) {
            return null;
        }

        return (

            <GameButtonsBox>
                <PlayDemoButtonComponent enabled={props.gameInfo.allowDemoButtonInSingleGame} onOpenAnonymousGame={props.onOpenAnonymousGame} />
                <InfoPageButtonComponent enabled={hasInfoPage} onOpenInfoPage={props.onOpenInfoPage} />
            </GameButtonsBox>
        );
    }

    const _renderAmount = () => {
        if (props.gameInfo.hasJackpot) {
            return _renderJackpot();
        }

        const formattedMinBaxBets = services.currency.formatMinMaxBet(props.gameInfo.minBet, props.gameInfo.maxBet);
        const hasMinMaxBets = props.gameInfo.allowMinMaxBet && formattedMinBaxBets;
        if (hasMinMaxBets) {
            return _renderMinBaxBet();
        }

        return null;
    }

    const _hasDetails = () => {
        return props.gameInfo.allowMinMaxBet || props.gameInfo.hasJackpot;
    }

    const _renderJackpot = () => {
        return (
            <JackpotAmountBox className='cy-game-jackpot-amount'>
                <GameJackpotComponent gameType={props.gameInfo.gameType} />
            </JackpotAmountBox>
        );
    }

    const _renderMinBaxBet = () => {
        const formattedMinBaxBets = services.currency.formatMinMaxBet(props.gameInfo.minBet, props.gameInfo.maxBet);
        return (
            <MinMaxBetAmountBox className="cy-game-min-max-bet">
                {formattedMinBaxBets}
            </MinMaxBetAmountBox>
        );
    }

    const _renderTitle = () => {
        const hasDetails = _hasDetails();
        if (props.hasTwoLinesTitle) {
            return <TwoRowsGameTitleContainerBox className={classNames({ "has-details": hasDetails })} hasDetails={hasDetails}>
                <GameTitleBox
                    className={classNames(CyClass('cy-game-title'), { [TWO_LINES_TITLE]: true })}
                    hasTwoLinesTitle={true}
                >
                    {props.gameInfo.title}
                </GameTitleBox>
            </TwoRowsGameTitleContainerBox>;
        } else {
            return <GameTitleBox
                className={classNames(CyClass('cy-game-title'), { [TWO_LINES_TITLE]: !hasDetails, "has-details": hasDetails })}
                hasTwoLinesTitle={false}
                ref={titleRef}
            >
                {props.gameInfo.title}
            </GameTitleBox>;
        }
    }

    const _renderGameInfoSection = () => {
        return (
            <GameInfoContainerBox className={classNames('cy-game-info-section', { [ENABLE_HOVER_ANIMATION]: _allowHoverDetails() })}>
                {_renderTitle()}
                {props.hasTwoLinesTitle || _hasDetails() ?
                    <AmountContainerBox className={classNames({ "has-two-lines-layout": props.hasTwoLinesTitle, "has-jp": props.gameInfo.hasJackpot, "has-min-max": props.gameInfo.allowMinMaxBet })}>{_renderAmount()}</AmountContainerBox>
                    : null}
            </GameInfoContainerBox>
        );
    }

    return (
        <GameDetailsContainerBox className="cy-game-details-container"
            squareGameIcon={props.squareGameIcon}>
            {_renderGameInfoSection()}
            {_renderGameButtons()}
        </GameDetailsContainerBox>
    );
});
